<template>
  <div
    ref="target"
    class="flex flex-col gap-3 rounded-md border border-obsidian-200 bg-white p-4 shadow-sm md:p-5"
  >
    <div class="grid grid-cols-1 gap-4 md:grid-cols-[1fr_auto]">
      <div class="flex items-center gap-3">
        <div
          class="flex size-10 shrink-0 items-center justify-center rounded-full bg-obsidian-100 text-sm font-semibold uppercase text-obsidian-700"
        >
          {{ customerInitials }}
        </div>
        <div class="flex flex-col">
          <div class="mb-1 truncate text-sm font-semibold text-obsidian-900">
            {{ customerName }}
          </div>
          <div class="flex flex-wrap items-center gap-1 text-xs text-obsidian-500">
            {{ formatDate(entry.createdDate, DateTime.DATE_MED) }} •
            {{ t(`status.${entry.status}`) }}
          </div>
        </div>
      </div>

      <div ref="starsTarget" class="flex gap-2 text-base">
        <span v-if="entry.rating" class="text-gold-500">{{ formatStars(entry.rating) }}</span>
        <span class="text-obsidian-500">
          {{ entry.rating ? t('starsOutOfFive', { value: entry.rating }) : t('notRatedYet') }}
        </span>
      </div>
    </div>

    <div class="text-sm font-medium text-obsidian-600">
      <span>{{ t('booking') }}: </span>
      <router-link
        v-if="entry.booking && $can('read', subject('Booking', entry.booking))"
        :to="{ name: 'bookingEdit', params: { id: entry.booking.id } }"
        class="text-link"
      >
        {{ entry.booking.bookingNumber ?? 'n/a' }}
      </router-link>
      <span v-else>-</span>
    </div>

    <p
      v-if="entry.comment"
      class="whitespace-pre-wrap break-words text-sm leading-relaxed text-obsidian-700"
    >
      {{ entry.comment }}
    </p>
  </div>
</template>

<script setup lang="ts">
import { formatDate } from '@/hooks/use-formatted-date';
import { useElementVisibility } from '@vueuse/core';
import { subject } from '@casl/ability';
import confetti from 'canvas-confetti';
import { DateTime } from 'luxon';
import { computed, ref, useTemplateRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import type { Review } from '@/entities/reviews/review.entity';

const { t } = useI18n();

const { entry } = defineProps<{
  entry: Review;
}>();

const customerName = computed(() => {
  const firstName = entry.booking?.customerData?.firstName ?? '';
  const lastName = entry.booking?.customerData?.lastName ?? '';
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || 'n/a';
});

const customerInitials = computed(() => {
  const firstInitial = entry.booking?.customerData?.firstName?.charAt(0) ?? '';
  const lastInitial = entry.booking?.customerData?.lastName?.charAt(0) ?? '';
  const initials = `${firstInitial}${lastInitial}`.toUpperCase();
  return initials || '?';
});

const formatStars = (rating: number) => {
  const filled = Math.max(0, Math.min(5, Math.round(rating)));
  return '★★★★★'.slice(0, filled) + '☆☆☆☆☆'.slice(0, 5 - filled);
};

const target = useTemplateRef('target');
const starsTarget = useTemplateRef('starsTarget');
const targetIsVisible = useElementVisibility(target);
const hasShownConfetti = ref(false);

const launchReviewConfetti = () => {
  const starsRect = starsTarget.value?.getBoundingClientRect();
  const origin =
    starsRect && window.innerWidth > 0 && window.innerHeight > 0
      ? {
          x: (starsRect.left + starsRect.width / 2) / window.innerWidth,
          y: (starsRect.top + starsRect.height / 2) / window.innerHeight,
        }
      : undefined;

  confetti({
    origin,
    colors: ['#CDAF74'],
  });
};

watch(targetIsVisible, (isVisible) => {
  if (!isVisible || hasShownConfetti.value || entry.rating !== 5) return;
  hasShownConfetti.value = true;
  launchReviewConfetti();
});
</script>

<i18n lang="json">
{
  "en": {
    "booking": "Booking",
    "status": {
      "PENDING": "Pending",
      "COMPLETED": "Completed"
    },
    "starsOutOfFive": "{value}/5",
    "notRatedYet": "Not rated yet"
  },
  "de": {
    "booking": "Buchung",
    "status": {
      "PENDING": "Offen",
      "COMPLETED": "Abgeschlossen"
    },
    "starsOutOfFive": "{value}/5",
    "notRatedYet": "Noch nicht bewertet"
  },
  "es": {
    "booking": "Reserva",
    "status": {
      "PENDING": "Pendiente",
      "COMPLETED": "Completada"
    },
    "starsOutOfFive": "{value}/5",
    "notRatedYet": "Sin valorar"
  }
}
</i18n>
